import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, TextField, Grid,
  TablePagination, Alert, CircularProgress, Snackbar, FormControl,
  InputLabel, Select, MenuItem, Autocomplete, Checkbox, FormControlLabel,
  FormHelperText
} from '@mui/material';
import { Edit, Delete, Search, Email } from '@mui/icons-material';
import { 
  getAllUsers, 
  updateUser, 
  deleteUser,
  generateInvite,
  getUserById,
  getUserGroups,
  updateUserRole,
  removeUserFromDepartment,
  removeUserFromTeam,
  getCurrentUser
} from '../api/services/userService/User';
import { getAllClients, getClientById } from '../api/services/userService/Client';
import { getAllDepartments, getDepartmentById } from '../api/services/userService/Department';
import { getAllTeams, getTeamById } from '../api/services/userService/Team';

export default function UserManagement({ userPermissions }) {
  const { keycloak } = useKeycloak();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGroups, setUserGroups] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    status: 0,
    isSuperAdmin: false
  });
  
  const [roleChanges, setRoleChanges] = useState({});
  
  const [inviteData, setInviteData] = useState({
    email: '',
    groupId: '',
    type: 1, // Default to TEAM (GROUP_TYPE_TEAM = 1)
    role: 1, // Default to member
    baseUrl: window.location.origin.includes('localhost') ? 'https://piosphere.de' : window.location.origin,
    isExistingUser: false,
    userId: '',
    selectedClient: null,
    selectedDepartment: null,
    selectedTeam: null,
    addToClient: true,
    addToDepartment: true,
    addToTeam: true
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [apiPage, setApiPage] = useState(1);
  
  // Snackbar notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteLink, setShowInviteLink] = useState(false);

  const [existingUsers, setExistingUsers] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);

  // Check if user can manage users
  const canManageUsers = () => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin || 
           (userPermissions.clientIds.length > 0) ||
           (userPermissions.departmentIds.length > 0) ||
           (userPermissions.teamIds.length > 0);
  };

  // Filter clients based on user permissions
  const filterClientsByPermissions = (clientsList) => {
    if (!userPermissions || userPermissions.isSuperAdmin) {
      return clientsList;
    }
    
    return clientsList.filter(client => 
      userPermissions.clientIds.includes(client.id)
    );
  };

  // Filter departments based on user permissions
  const filterDepartmentsByPermissions = (departmentsList) => {
    if (!userPermissions || userPermissions.isSuperAdmin) {
      return departmentsList;
    }
    
    return departmentsList.filter(dept => {
      // User has direct access to this department
      if (userPermissions.departmentIds.includes(dept.id)) {
        return true;
      }
      
      // User has access to the client that owns this department
      if (userPermissions.clientIds.includes(dept.clientId)) {
        return true;
      }
      
      return false;
    });
  };

  // Filter teams based on user permissions
  const filterTeamsByPermissions = (teamsList) => {
    if (!userPermissions || userPermissions.isSuperAdmin) {
      return teamsList;
    }
    
    return teamsList.filter(team => {
      // User has direct access to this team
      if (userPermissions.teamIds.includes(team.id)) {
        return true;
      }
      
      // User has access to the department that owns this team
      if (userPermissions.departmentIds.includes(team.departmentId)) {
        return true;
      }
      
      return false;
    });
  };

  const fetchClients = async () => {
    try {
      await keycloak.updateToken(30);
      
      let clients = [];
      
      if (userPermissions?.isSuperAdmin) {
        const response = await getAllClients({
          pagination: {
            page: 1,
            pageSize: 100
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        }, keycloak.token);
        
        if (response.success) {
          clients = response.clients;
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for UserManagement:', userData);

        if (userData.client) {
          const clientResponse = await getClientById(userData.client.client.id, keycloak.token);
          if (clientResponse.success) {
            clients = [clientResponse.client];
          }
        }
      }
      
      const filteredClients = filterClientsByPermissions(clients);
      setClients(filteredClients);
    } catch (err) {
      setError(err.message || 'Failed to fetch clients');
    }
  };

  const fetchDepartments = async (clientId = '') => {
    try {
      await keycloak.updateToken(30);
      
      let departments = [];
      
      if (userPermissions?.isSuperAdmin) {
        const params = {
          pagination: {
            page: 1,
            pageSize: 500
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        };
        
        console.log('Fetching departments with params:', params);
        const response = await getAllDepartments(params, keycloak.token);
        console.log('Departments response:', response);
        
        if (response.success) {
          departments = response.departments;
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for departments in UserManagement:', userData);

        if (userData.departments && userData.departments.length > 0) {
          const departmentPromises = userData.departments.map(deptData => 
            getDepartmentById(deptData.department.id, keycloak.token)
          );
          const departmentResponses = await Promise.all(departmentPromises);
          
          departmentResponses.forEach((response) => {
            if (response.success) {
              const department = response.department;
              department.clientId = userData.client ? userData.client.client.id : null;
              departments.push(department);
            }
          });
        }
      }
      
      const filteredDepartments = filterDepartmentsByPermissions(departments);
      setDepartments(filteredDepartments);
      return filteredDepartments;
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setError(err.message || 'Failed to fetch departments');
      return [];
    }
  };

  const fetchTeams = async (departmentId = '') => {
    try {
      await keycloak.updateToken(30);
      
      let teams = [];
      
      if (userPermissions?.isSuperAdmin) {
        const params = {
          pagination: {
            page: 1,
            pageSize: 1000
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        };
        
        console.log('Fetching teams with params:', params);
        const response = await getAllTeams(params, keycloak.token);
        console.log('Teams response:', response);
        
        if (response.success) {
          teams = response.teams;
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for teams in UserManagement:', userData);

        if (userData.teams && userData.teams.length > 0) {
          const teamPromises = userData.teams.map(teamData => 
            getTeamById(teamData.team.id, keycloak.token)
          );
          const teamResponses = await Promise.all(teamPromises);
          
          teamResponses.forEach((response, index) => {
            if (response.success) {
              const team = response.team;
              const teamData = userData.teams[index];
              team.departmentId = teamData.team.departmentId;
              teams.push(team);
            }
          });
        }
      }
      
      const filteredTeams = filterTeamsByPermissions(teams);
      setTeams(filteredTeams);
      return filteredTeams;
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      setError(err.message || 'Failed to fetch teams');
      return [];
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      await keycloak.updateToken(30);
      
      let usersData = [];
      
      if (userPermissions?.isSuperAdmin) {
        const params = {
          pagination: {
            page: page + 1,
            pageSize: rowsPerPage
          },
          sorting: {
            sort_by: 'SORT_BY_NAME',
            order: 'SORT_ORDER_ASCENDING'
          }
        };
        
        if (searchTerm) {
          params.query = searchTerm;
        }
        
        if (selectedTeamId) {
          params.team_id = selectedTeamId;
        } else if (selectedDepartmentId) {
          params.department_id = selectedDepartmentId;
        } else if (selectedClientId) {
          params.client_id = selectedClientId;
        }
        
        console.log('Fetching users with params:', params);
        const response = await getAllUsers(params, keycloak.token);
        console.log('Users response:', response);
        
        if (response.success) {
          const processedUsers = (response.users || []).map(user => ({
            ...user,
            fullName: user.full_name || user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            clientId: user.clients && user.clients.length > 0 ? user.clients[0].id : undefined,
            departmentId: user.departments && user.departments.length > 0 ? user.departments[0].id : undefined,
            teamId: user.teams && user.teams.length > 0 ? user.teams[0].id : undefined
          }));
          
          setUsers(processedUsers);
          
          const totalItems = response.totalCount || 0;
          const apiPageVal = response.page || 1;
          const apiPageCount = response.pageCount || 1;
          
          setTotalCount(totalItems);
          setApiPage(apiPageVal);
          setPageCount(apiPageCount);
        } else {
          setError(response.error);
        }
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (!currentUserResponse.success) {
          throw new Error(currentUserResponse.error || 'Failed to fetch current user data');
        }

        const userData = currentUserResponse.user;
        console.log('Current user data for UserManagement:', userData);

        const allUsers = new Map();
        
        const currentUserRecord = {
          id: userData.id,
          fullName: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.email,
          full_name: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.email,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          company: userData.company,
          position: userData.position,
          isSuperAdmin: userData.superAdmin,
          superAdmin: userData.superAdmin,
          enabled: true,
          status: userData.status || 0,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          clientIds: userData.client ? [userData.client.client.id] : [],
          clientId: userData.client ? userData.client.client.id : null,
          clients: userData.client ? [{ id: userData.client.client.id, name: userData.client.client.name || 'Unknown Client', role: userData.client.client.role }] : [],
          departmentIds: userData.departments ? userData.departments.map(d => d.department.id) : [],
          departmentId: userData.departments && userData.departments.length > 0 ? userData.departments[0].department.id : null,
          departments: userData.departments ? userData.departments.map(d => ({ id: d.department.id, name: d.department.name || 'Unknown Department', role: d.department.role })) : [],
          teamIds: userData.teams ? userData.teams.map(t => t.team.id) : [],
          teamId: userData.teams && userData.teams.length > 0 ? userData.teams[0].team.id : null,
          teams: userData.teams ? userData.teams.map(t => ({ id: t.team.id, name: t.team.name || 'Unknown Team', role: t.team.role })) : []
        };
        
        allUsers.set(userData.id, currentUserRecord);
        
        if (userData.client && userData.client.admins) {
          userData.client.admins.forEach(admin => {
            if (!allUsers.has(admin.id)) {
              allUsers.set(admin.id, {
                id: admin.id,
                fullName: admin.fullName,
                full_name: admin.fullName,
                email: admin.email,
                firstName: admin.fullName ? admin.fullName.split(' ')[0] : admin.email,
                lastName: admin.fullName ? admin.fullName.split(' ').slice(1).join(' ') : '',
                isSuperAdmin: false,
                superAdmin: false,
                enabled: true,
                status: 0,
                clientIds: [userData.client.client.id],
                clientId: userData.client.client.id,
                clients: [{ id: userData.client.client.id, name: userData.client.client.name || 'Unknown Client', role: 'admin' }],
                departmentIds: [],
                teamIds: [],
                departments: [],
                teams: []
              });
            } else {
              const existingUser = allUsers.get(admin.id);
              if (!existingUser.clients.some(c => c.id === userData.client.client.id)) {
                existingUser.clients.push({ id: userData.client.client.id, name: userData.client.client.name || 'Unknown Client', role: 'admin' });
                existingUser.clientIds.push(userData.client.client.id);
              }
            }
          });
        }

        if (userData.departments && userData.departments.length > 0) {
          userData.departments.forEach(deptData => {
            if (deptData.admins) {
              deptData.admins.forEach(admin => {
                if (!allUsers.has(admin.id)) {
                  allUsers.set(admin.id, {
                    id: admin.id,
                    fullName: admin.fullName,
                    full_name: admin.fullName,
                    email: admin.email,
                    firstName: admin.fullName ? admin.fullName.split(' ')[0] : admin.email,
                    lastName: admin.fullName ? admin.fullName.split(' ').slice(1).join(' ') : '',
                    isSuperAdmin: false,
                    superAdmin: false,
                    enabled: true,
                    status: 0,
                    clientIds: userData.client ? [userData.client.client.id] : [],
                    clientId: userData.client ? userData.client.client.id : null,
                    clients: userData.client ? [{ id: userData.client.client.id, name: userData.client.client.name || 'Unknown Client', role: 'member' }] : [],
                    departmentIds: [deptData.department.id],
                    departmentId: deptData.department.id,
                    departments: [{ id: deptData.department.id, name: deptData.department.name || 'Unknown Department', role: admin.id === userData.id ? deptData.department.role : 'admin' }],
                    teamIds: [],
                    teams: []
                  });
                } else {
                  const existingUser = allUsers.get(admin.id);
                  if (!existingUser.departments.some(d => d.id === deptData.department.id)) {
                    existingUser.departmentIds.push(deptData.department.id);
                    existingUser.departments.push({ 
                      id: deptData.department.id, 
                      name: deptData.department.name || 'Unknown Department', 
                      role: admin.id === userData.id ? deptData.department.role : 'admin' 
                    });
                    if (!existingUser.departmentId) {
                      existingUser.departmentId = deptData.department.id;
                    }
                  }
                }
              });
            }
          });
        }

        if (userData.teams && userData.teams.length > 0) {
          userData.teams.forEach(teamData => {
            if (teamData.admins) {
              teamData.admins.forEach(admin => {
                if (!allUsers.has(admin.id)) {
                  allUsers.set(admin.id, {
                    id: admin.id,
                    fullName: admin.fullName,
                    full_name: admin.fullName,
                    email: admin.email,
                    firstName: admin.fullName ? admin.fullName.split(' ')[0] : admin.email,
                    lastName: admin.fullName ? admin.fullName.split(' ').slice(1).join(' ') : '',
                    isSuperAdmin: false,
                    superAdmin: false,
                    enabled: true,
                    status: 0,
                    clientIds: userData.client ? [userData.client.client.id] : [],
                    clientId: userData.client ? userData.client.client.id : null,
                    clients: userData.client ? [{ id: userData.client.client.id, name: userData.client.client.name || 'Unknown Client', role: 'member' }] : [],
                    departmentIds: userData.departments ? userData.departments.map(d => d.department.id) : [],
                    departmentId: userData.departments && userData.departments.length > 0 ? userData.departments[0].department.id : null,
                    departments: userData.departments ? userData.departments.map(d => ({ id: d.department.id, name: d.department.name || 'Unknown Department', role: 'member' })) : [],
                    teamIds: [teamData.team.id],
                    teamId: teamData.team.id,
                    teams: [{ id: teamData.team.id, name: teamData.team.name || 'Unknown Team', role: admin.id === userData.id ? teamData.team.role : 'admin' }]
                  });
                } else {
                  const existingUser = allUsers.get(admin.id);
                  if (!existingUser.teams.some(t => t.id === teamData.team.id)) {
                    existingUser.teamIds.push(teamData.team.id);
                    existingUser.teams.push({ 
                      id: teamData.team.id, 
                      name: teamData.team.name || 'Unknown Team', 
                      role: admin.id === userData.id ? teamData.team.role : 'admin' 
                    });
                    if (!existingUser.teamId) {
                      existingUser.teamId = teamData.team.id;
                    }
                  }
                }
              });
            }
          });
        }

        usersData = Array.from(allUsers.values());
        console.log('Processed users for non-super admin UserManagement:', usersData);

        let filteredUsers = usersData;

        if (searchTerm) {
          filteredUsers = filteredUsers.filter(user => 
            user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        if (selectedTeamId) {
          filteredUsers = filteredUsers.filter(user => 
            user.teamIds?.includes(selectedTeamId) ||
            user.teams?.some(t => t.id === selectedTeamId)
          );
        } else if (selectedDepartmentId) {
          filteredUsers = filteredUsers.filter(user => 
            user.departmentIds?.includes(selectedDepartmentId) ||
            user.departments?.some(d => d.id === selectedDepartmentId)
          );
        } else if (selectedClientId) {
          filteredUsers = filteredUsers.filter(user => 
            user.clientIds?.includes(selectedClientId) ||
            user.clients?.some(c => c.id === selectedClientId)
          );
        }

        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

        setUsers(paginatedUsers);
        setTotalCount(filteredUsers.length);
        setApiPage(page + 1);
        setPageCount(Math.ceil(filteredUsers.length / rowsPerPage));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchDepartments();
    fetchTeams();
    fetchUsers();
  }, [keycloak.token]);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, selectedClientId, selectedDepartmentId, selectedTeamId, searchTerm, keycloak.token]);

  const handleSearch = () => {
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Page changes will trigger the fetchUsers effect
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
    // These changes will trigger the fetchUsers effect
  };

  const handleClientFilterChange = (event) => {
    const clientId = event.target.value;
    setSelectedClientId(clientId);
    setSelectedDepartmentId('');
    setSelectedTeamId('');
    setPage(0);
    
    // Update filtered departments based on selected client
    if (clientId) {
      const clientDepartments = departments.filter(dept => dept.clientId === clientId);
      setFilteredDepartments(clientDepartments);
    } else {
      setFilteredDepartments(departments);
    }
    setFilteredTeams(teams.filter(team => !selectedDepartmentId || team.departmentId === selectedDepartmentId));
  };

  const handleDepartmentFilterChange = (event) => {
    const departmentId = event.target.value;
    setSelectedDepartmentId(departmentId);
    setSelectedTeamId('');
    setPage(0);
    
    // Update filtered teams based on selected department
    if (departmentId) {
      const departmentTeams = teams.filter(team => team.departmentId === departmentId);
      setFilteredTeams(departmentTeams);
    } else {
      // If no department selected, show teams for the selected client (if any)
      if (selectedClientId) {
        const clientDepartments = departments.filter(dept => dept.clientId === selectedClientId);
        const clientDepartmentIds = clientDepartments.map(dept => dept.id);
        const clientTeams = teams.filter(team => clientDepartmentIds.includes(team.departmentId));
        setFilteredTeams(clientTeams);
      } else {
        setFilteredTeams(teams);
      }
    }
  };

  const handleTeamFilterChange = (event) => {
    setSelectedTeamId(event.target.value);
    setPage(0);
  };

  // Initialize filtered lists when data is loaded
  useEffect(() => {
    if (selectedClientId) {
      const clientDepartments = departments.filter(dept => dept.clientId === selectedClientId);
      setFilteredDepartments(clientDepartments);
    } else {
      setFilteredDepartments(departments);
    }
  }, [departments, selectedClientId]);

  useEffect(() => {
    if (selectedDepartmentId) {
      const departmentTeams = teams.filter(team => team.departmentId === selectedDepartmentId);
      setFilteredTeams(departmentTeams);
    } else if (selectedClientId) {
      const clientDepartments = departments.filter(dept => dept.clientId === selectedClientId);
      const clientDepartmentIds = clientDepartments.map(dept => dept.id);
      const clientTeams = teams.filter(team => clientDepartmentIds.includes(team.departmentId));
      setFilteredTeams(clientTeams);
    } else {
      setFilteredTeams(teams);
    }
  }, [teams, departments, selectedClientId, selectedDepartmentId]);

  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      status: 0,
      isSuperAdmin: false
    });
    setOpenDialog(true);
  };

  const openEditDialog = async (userId) => {
    setLoading(true);
    try {
      await keycloak.updateToken(30);
      
      const response = await getUserById(userId, keycloak.token);
      
      if (response.success) {
        const userData = response.user;
        setCurrentUser(userData);
        
        const formDataInit = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          company: userData.company || '',
          position: userData.position || '',
          status: userData.status || 0,
          isSuperAdmin: userData.superAdmin || userData.isSuperAdmin || false
        };
        
        try {
          const groupsResponse = await getUserGroups(userId, keycloak.token);
          if (groupsResponse.success) {
            setUserGroups(groupsResponse);
            
            const initialRoleChanges = {};
            if (groupsResponse.clients) {
              groupsResponse.clients.forEach(client => {
                initialRoleChanges[client.id] = client.client_role || 1;
              });
            }
            if (groupsResponse.departments) {
              groupsResponse.departments.forEach(department => {
                initialRoleChanges[department.id] = department.department_role || 1;
              });
            }
            if (groupsResponse.teams) {
              groupsResponse.teams.forEach(team => {
                initialRoleChanges[team.id] = team.team_role || 1;
              });
            }
            setRoleChanges(initialRoleChanges);
          }
        } catch (err) {
          console.error('Failed to fetch user groups:', err);
        }
        
        setFormData(formDataInit);
        setIsEdit(true);
        setOpenDialog(true);
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to load user details',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const openInviteUserDialog = async () => {
    try {
      await keycloak.updateToken(30);
      
      const response = await getAllUsers({
        pagination: {
          page: 1,
          pageSize: 100
        },
        sorting: {
          sort_by: 'SORT_BY_NAME',
          order: 'SORT_ORDER_ASCENDING'
        }
      }, keycloak.token);
      
      if (response.success) {
        setExistingUsers(response.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch existing users:', err);
    }
    
    setInviteData({
      email: '',
      groupId: '',
      type: 1, // Default to TEAM (GROUP_TYPE_TEAM = 1)
      role: 1, // Default to member
      baseUrl: window.location.origin.includes('localhost') ? 'https://piosphere.de' : window.location.origin,
      isExistingUser: false,
      userId: '',
      selectedClient: null,
      selectedDepartment: null,
      selectedTeam: null,
      addToClient: true,
      addToDepartment: true,
      addToTeam: true
    });
    
    setOpenInviteDialog(true);
  };

  const handleDeleteClick = (user) => {
    setCurrentUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRoleChanges({});
  };

  const handleCloseInviteDialog = () => {
    setOpenInviteDialog(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInviteInputChange = (e) => {
    const { name, value } = e.target;
    setInviteData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset hierarchical selections when changing user type
    if (name === 'isExistingUser') {
      setInviteData(prev => ({
        ...prev,
        userId: '',
        email: '',
        selectedClient: null,
        selectedDepartment: null,
        selectedTeam: null
      }));
    }
  };

  const handleExistingUserChange = (event, user) => {
    if (user) {
      setInviteData(prev => ({
        ...prev,
        userId: user.id,
        email: user.email
      }));
    } else {
      setInviteData(prev => ({
        ...prev,
        userId: '',
        email: ''
      }));
    }
  };
  
  const handleClientChange = (event, client) => {
    if (client) {
      setInviteData(prev => ({
        ...prev,
        selectedClient: client,
      }));
    } else {
      setInviteData(prev => ({
        ...prev,
        selectedClient: null,
      }));
    }
  };
  
  const handleDepartmentChange = (event, department) => {
    if (department) {
      setInviteData(prev => ({
        ...prev,
        selectedDepartment: department,
      }));
    } else {
      setInviteData(prev => ({
        ...prev,
        selectedDepartment: null,
      }));
    }
  };
  
  const handleTeamChange = (event, team) => {
    setInviteData(prev => ({
      ...prev,
      selectedTeam: team
    }));
  };

  const handleUserSubmit = async () => {
    if (!formData.firstName || !formData.lastName) {
      setNotification({
        open: true,
        message: 'First name and last name are required',
        severity: 'error'
      });
      return;
    }

    try {
      await keycloak.updateToken(30);
      
      const userData = {
        id: currentUser?.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        company: formData.company,
        position: formData.position,
        status: formData.status
      };
      
      const response = await updateUser(userData, keycloak.token);
      
      if (response.success) {
        if (formData.isSuperAdmin !== (currentUser?.isSuperAdmin || false)) {
          try {
            let groupId = '';
            let groupType = 4;
            
            if (userGroups?.clients?.length > 0) {
              groupId = userGroups.clients[0].id;
              groupType = 3;
            } else if (userGroups?.departments?.length > 0) {
              groupId = userGroups.departments[0].id;
              groupType = 2;  
            } else if (userGroups?.teams?.length > 0) {
              groupId = userGroups.teams[0].id;
              groupType = 1;
            }
            
            const roleResponse = await updateUserRole(
              currentUser.id, 
              groupId, 
              groupType, 
              1,
              keycloak.token, 
              formData.isSuperAdmin
            );
            
            if (!roleResponse.success) {
              setNotification({
                open: true,
                message: `User updated but failed to update super admin status: ${roleResponse.error}`,
                severity: 'warning'
              });
            }
          } catch (err) {
            console.error('Failed to update super admin status:', err);
            setNotification({
              open: true,
              message: 'User updated but failed to update super admin status',
              severity: 'warning'
            });
          }
        }
        
        let roleUpdateErrors = [];
        for (const [groupId, newRole] of Object.entries(roleChanges)) {
          try {
            let groupType = 1;
            if (userGroups?.clients?.some(c => c.id === groupId)) {
              groupType = 3;
            } else if (userGroups?.departments?.some(d => d.id === groupId)) {
              groupType = 2;
            } else if (userGroups?.teams?.some(t => t.id === groupId)) {
              groupType = 1;
            }
            
            let currentRole = 1;
            if (groupType === 3) {
              const client = userGroups.clients?.find(c => c.id === groupId);
              currentRole = client?.client_role || 1;
            } else if (groupType === 2) {
              const department = userGroups.departments?.find(d => d.id === groupId);
              currentRole = department?.department_role || 1;
            } else if (groupType === 1) {
              const team = userGroups.teams?.find(t => t.id === groupId);
              currentRole = team?.team_role || 1;
            }
            
            if (parseInt(newRole) !== currentRole) {
              const roleResponse = await updateUserRole(
                currentUser.id,
                groupId,
                groupType,
                parseInt(newRole),
                keycloak.token
              );
              
              if (!roleResponse.success) {
                roleUpdateErrors.push(`Failed to update role for group ${groupId}: ${roleResponse.error}`);
              }
            }
          } catch (err) {
            console.error('Failed to update role for group:', groupId, err);
            roleUpdateErrors.push(`Failed to update role for group ${groupId}: ${err.message}`);
          }
        }
        
        if (roleUpdateErrors.length > 0) {
          setNotification({
            open: true,
            message: `User updated but some role changes failed: ${roleUpdateErrors.join(', ')}`,
            severity: 'warning'
          });
        } else {
          setNotification({
            open: true,
            message: 'User and roles updated successfully',
            severity: 'success'
          });
        }
        
        fetchUsers();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to update user',
          severity: 'error'
        });
      }
      
      setOpenDialog(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleInviteSubmit = async () => {
    try {
      await keycloak.updateToken(30);
      
      if (inviteData.isExistingUser) {
        if (!inviteData.userId) {
          setNotification({
            open: true,
            message: 'Please select a user',
            severity: 'error'
          });
          return;
        }
      } else {
        if (!inviteData.email) {
          setNotification({
            open: true,
            message: 'Email is required',
            severity: 'error'
          });
          return;
        }
      }
      
      if (inviteData.isExistingUser) {
        let successMessages = [];
        let errorMessages = [];
        let anySelectionMade = false;
        
        const sendInvite = async (groupId, groupType, role) => {
          try {
            const inviteParams = {
              email: inviteData.email,
              groupId: groupId,
              type: groupType,
              role: role,
              baseUrl: inviteData.baseUrl || window.location.origin
            };
            
            const groupTypeName = groupType === 1 ? 'team' : 
                                 groupType === 2 ? 'department' : 'client';
            
            console.log(`Sending invite for ${groupTypeName} with params:`, inviteParams);
            const response = await generateInvite(inviteParams, keycloak.token);
            console.log(`${groupTypeName} invite response:`, response);
            
            if (response.success) {
              successMessages.push(`User added to ${groupTypeName} successfully`);
              anySelectionMade = true;
              return true;
            } else {
              errorMessages.push(`Failed to add to ${groupTypeName}: ${response.error}`);
              return false;
            }
          } catch (err) {
            console.error('Invite error:', err);
            errorMessages.push(err.message || 'An error occurred');
            return false;
          }
        };
        
        if (inviteData.selectedClient && inviteData.addToClient) {
          await sendInvite(
            inviteData.selectedClient.id,
            3,
            inviteData.role
          );
        }
        
        if (inviteData.selectedDepartment && inviteData.addToDepartment) {
          await sendInvite(
            inviteData.selectedDepartment.id,
            2,
            inviteData.role
          );
        }
        
        if (inviteData.selectedTeam && inviteData.addToTeam) {
          await sendInvite(
            inviteData.selectedTeam.id,
            1,
            inviteData.role
          );
        }
        
        if (!anySelectionMade && 
            (!inviteData.selectedClient || !inviteData.addToClient) &&
            (!inviteData.selectedDepartment || !inviteData.addToDepartment) &&
            (!inviteData.selectedTeam || !inviteData.addToTeam)) {
          setNotification({
            open: true,
            message: 'Please select at least one group to add the user to',
            severity: 'error'
          });
          return;
        }
        
        if (successMessages.length > 0) {
          setNotification({
            open: true,
            message: successMessages.join(', '),
            severity: 'success'
          });
          fetchUsers();
        }
        
        if (errorMessages.length > 0) {
          setTimeout(() => {
            setNotification({
              open: true,
              message: errorMessages.join(', '),
              severity: 'error'
            });
          }, 1000);
        }
      } else {
        if (!inviteData.groupId) {
          setNotification({
            open: true,
            message: 'Please select a group',
            severity: 'error'
          });
          return;
        }
        
        const response = await generateInvite(inviteData, keycloak.token);
        
        if (response.success) {
          setNotification({
            open: true,
            message: 'Invitation sent successfully',
            severity: 'success'
          });
          
          if (response.inviteLink) {
            setInviteLink(response.inviteLink);
            setShowInviteLink(true);
          }
        } else {
          setNotification({
            open: true,
            message: response.error || 'Failed to send invitation',
            severity: 'error'
          });
        }
      }
      
      setOpenInviteDialog(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await keycloak.updateToken(30);
      
      console.log("Deleting user with ID:", currentUser.id);
      console.log("Token:", keycloak.token);
      const response = await deleteUser(currentUser.id, keycloak.token);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'User deleted successfully',
          severity: 'success'
        });
        fetchUsers();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to delete user',
          severity: 'error'
        });
      }
      
      setOpenDeleteDialog(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleCloseInviteLinkDialog = () => {
    setShowInviteLink(false);
    setInviteLink('');
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setNotification({
      open: true,
      message: 'Invitation link copied to clipboard',
      severity: 'success'
    });
  };

  // Get client name by ID
  const getClientName = (clientId) => {
    if (!clientId) return 'Not Assigned';
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  // Get department name by ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'Not Assigned';
    const department = departments.find(d => d.id === departmentId);
    return department ? department.name : 'Unknown Department';
  };

  // Get team name by ID
  const getTeamName = (teamId) => {
    if (!teamId) return 'Not Assigned';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const getGroupName = (groupId, type) => {
    switch (type) {
      case 1: // TEAM
        const team = teams.find(t => t.id === groupId);
        return team ? team.name : 'Unknown Team';
      case 2: // DEPARTMENT
        const department = departments.find(d => d.id === groupId);
        return department ? department.name : 'Unknown Department';
      case 3: // CLIENT
        const client = clients.find(c => c.id === groupId);
        return client ? client.name : 'Unknown Client';
      default:
        return 'Unknown Group';
    }
  };

  const updateUserRoleFunction = async (userId, groupId, groupType, roleValue) => {
    try {
      await keycloak.updateToken(30);
      
      const response = await updateUserRole(userId, groupId, groupType, roleValue, keycloak.token);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'Role updated successfully',
          severity: 'success'
        });
        fetchUsers();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to update role',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  // Add new handlers for removing users from departments and teams
  const handleRemoveFromDepartment = async (departmentId) => {
    if (!currentUser || !departmentId) return;
    
    try {
      await keycloak.updateToken(30);
      
      const response = await removeUserFromDepartment(departmentId, currentUser.id, keycloak.token);
      
      if (response.success) {
        const groupsResponse = await getUserGroups(currentUser.id, keycloak.token);
        if (groupsResponse.success) {
          setUserGroups(groupsResponse);
        }
        
        setNotification({
          open: true,
          message: 'User removed from department successfully',
          severity: 'success'
        });
        
        fetchUsers();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to remove user from department',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleRemoveFromTeam = async (teamId) => {
    if (!currentUser || !teamId) return;
    
    try {
      await keycloak.updateToken(30);
      
      const response = await removeUserFromTeam(teamId, currentUser.id, keycloak.token);
      
      if (response.success) {
        const groupsResponse = await getUserGroups(currentUser.id, keycloak.token);
        if (groupsResponse.success) {
          setUserGroups(groupsResponse);
        }
        
        setNotification({
          open: true,
          message: 'User removed from team successfully',
          severity: 'success'
        });
        
        fetchUsers();
      } else {
        setNotification({
          open: true,
          message: response.error || 'Failed to remove user from team',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">User Management</Typography>
        <Button 
          variant="outlined" 
          startIcon={<Email />} 
          onClick={openInviteUserDialog}
        >
          Invite User
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search users"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleSearch}>
                  <Search />
                </IconButton>
              )
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel id="client-filter-label">Filter by Client</InputLabel>
            <Select
              labelId="client-filter-label"
              value={selectedClientId}
              label="Filter by Client"
              onChange={handleClientFilterChange}
            >
              <MenuItem value="">
                <em>All Clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth disabled={!selectedClientId}>
            <InputLabel id="department-filter-label">Filter by Department</InputLabel>
            <Select
              labelId="department-filter-label"
              value={selectedDepartmentId}
              label="Filter by Department"
              onChange={handleDepartmentFilterChange}
            >
              <MenuItem value="">
                <em>All Departments{selectedClientId ? ' for Selected Client' : ''}</em>
              </MenuItem>
              {filteredDepartments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth disabled={!selectedDepartmentId}>
            <InputLabel id="team-filter-label">Filter by Team</InputLabel>
            <Select
              labelId="team-filter-label"
              value={selectedTeamId}
              label="Filter by Team"
              onChange={handleTeamFilterChange}
            >
              <MenuItem value="">
                <em>All Teams{selectedDepartmentId ? ' for Selected Department' : ''}</em>
              </MenuItem>
              {filteredTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.full_name || user.fullName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.clients && user.clients.length > 0
                          ? user.clients.map(client => client.name).join(", ")
                          : getClientName(user.clientId)}
                      </TableCell>
                      <TableCell>
                        {user.departments && user.departments.length > 0
                          ? user.departments.map(dept => dept.name).join(", ")
                          : getDepartmentName(user.departmentId)}
                      </TableCell>
                      <TableCell>
                        {user.teams && user.teams.length > 0
                          ? user.teams.map(team => team.name).join(", ")
                          : getTeamName(user.teamId)}
                      </TableCell>
                      <TableCell>
                        {user.clients && user.clients.length > 0 && user.clients[0].role ||
                         user.departments && user.departments.length > 0 && user.departments[0].role ||
                         user.teams && user.teams.length > 0 && user.teams[0].role ||
                         user.role || 'Member'}
                      </TableCell>
                      <TableCell>{user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}</TableCell>
                      <TableCell>{user.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEditDialog(user.id)}>
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(user)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) => {
              if (count === 0) return "0 of 0";
              
              // When client-side filtering is active, adjust the display
              if (selectedClientId || selectedDepartmentId || selectedTeamId) {
                const adjustedFrom = (page * rowsPerPage) + 1;
                const adjustedTo = Math.min((page + 1) * rowsPerPage, count);
                return `${adjustedFrom}-${adjustedTo} of ${count}`;
              }
              
              const currentPage = apiPage;
              const totalPages = pageCount;
              const adjustedFrom = from === 0 ? 1 : from;
              const adjustedTo = Math.min(to, count);
              return `${adjustedFrom}-${adjustedTo} of ${count} (Page ${currentPage} of ${totalPages})`;
            }}
          />
        </>
      )}

      {/* Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                name="firstName"
                label="First Name"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.firstName}
                onChange={handleFormInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastName"
                label="Last Name"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.lastName}
                onChange={handleFormInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={handleFormInputChange}
                disabled={isEdit}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="phone"
                label="Phone"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.phone}
                onChange={handleFormInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="company"
                label="Company"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.company}
                onChange={handleFormInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="position"
                label="Position"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.position}
                onChange={handleFormInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleFormInputChange}
                >
                  <MenuItem value={0}>Active</MenuItem>
                  <MenuItem value={1}>Pending</MenuItem>
                  <MenuItem value={2}>Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isEdit && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isSuperAdmin}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        isSuperAdmin: e.target.checked
                      }))}
                      name="isSuperAdmin"
                    />
                  }
                  label="Super Admin"
                />
              </Grid>
            )}
          </Grid>
          
          {isEdit && userGroups && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                Group Memberships & Roles
              </Typography>
              
              {userGroups.clients && userGroups.clients.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    Clients
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userGroups.clients.map(client => (
                      <Paper key={client.id} sx={{ p: 2}}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {getGroupName(client.id, 3)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Role</InputLabel>
                              <Select
                                value={roleChanges[client.id] || 1}
                                label="Role"
                                onChange={(e) => {
                                  const newRole = e.target.value;
                                  setRoleChanges(prev => ({
                                    ...prev,
                                    [client.id]: newRole
                                  }));
                                }}
                              >
                                <MenuItem value={1}>Member</MenuItem>
                                <MenuItem value={2}>Admin</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
              
              {userGroups.departments && userGroups.departments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    Departments
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userGroups.departments.map(department => (
                      <Paper key={department.id} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {getGroupName(department.id, 2)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Role</InputLabel>
                              <Select
                                value={roleChanges[department.id] || 1}
                                label="Role"
                                onChange={(e) => {
                                  const newRole = e.target.value;
                                  setRoleChanges(prev => ({
                                    ...prev,
                                    [department.id]: newRole
                                  }));
                                }}
                              >
                                <MenuItem value={1}>Member</MenuItem>
                                <MenuItem value={2}>Admin</MenuItem>
                              </Select>
                            </FormControl>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="error"
                              onClick={() => handleRemoveFromDepartment(department.id)}
                              sx={{ minWidth: 'auto', px: 2 }}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
              
              {userGroups.teams && userGroups.teams.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    Teams
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userGroups.teams.map(team => (
                      <Paper key={team.id} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {getGroupName(team.id, 1)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Role</InputLabel>
                              <Select
                                value={roleChanges[team.id] || 1}
                                label="Role"
                                onChange={(e) => {
                                  const newRole = e.target.value;
                                  setRoleChanges(prev => ({
                                    ...prev,
                                    [team.id]: newRole
                                  }));
                                }}
                              >
                                <MenuItem value={1}>Member</MenuItem>
                                <MenuItem value={2}>Lead</MenuItem>
                              </Select>
                            </FormControl>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="error"
                              onClick={() => handleRemoveFromTeam(team.id)}
                              sx={{ minWidth: 'auto', px: 2 }}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
              
              {(!userGroups.clients?.length && !userGroups.departments?.length && !userGroups.teams?.length) && (
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" color="text.secondary">
                    This user is not assigned to any groups yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Use the "Invite User" feature to add them to groups.
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleUserSubmit} variant="contained" color="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={openInviteDialog} onClose={handleCloseInviteDialog} maxWidth="md" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inviteData.isExistingUser}
                    onChange={(e) => setInviteData(prev => ({
                      ...prev,
                      isExistingUser: e.target.checked
                    }))}
                    name="isExistingUser"
                  />
                }
                label="Existing User"
              />
            </Grid>
            
            {inviteData.isExistingUser ? (
              <Grid item xs={12}>
                <Autocomplete
                  id="existing-user-select"
                  options={existingUsers}
                  getOptionLabel={(user) => `${user.full_name || user.fullName} (${user.email})`}
                  onChange={handleExistingUserChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Select User" required fullWidth />
                  )}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={inviteData.email}
                  onChange={handleInviteInputChange}
                  required
                />
              </Grid>
            )}
            
            {inviteData.isExistingUser ? (
              // Hierarchical selection for existing users
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Add user to organization hierarchy:
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={10}>
                  <Autocomplete
                    id="client-select"
                    options={clients}
                    getOptionLabel={(client) => client.name}
                    onChange={handleClientChange}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Client" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inviteData.addToClient}
                        onChange={(e) => setInviteData(prev => ({
                          ...prev,
                          addToClient: e.target.checked
                        }))}
                        disabled={!inviteData.selectedClient}
                      />
                    }
                    label="Add"
                  />
                </Grid>
                
                <Grid item xs={12} sm={10}>
                  <Autocomplete
                    id="department-select"
                    options={departments}
                    getOptionLabel={(department) => department.name}
                    onChange={handleDepartmentChange}
                    value={inviteData.selectedDepartment}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Department" fullWidth />
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inviteData.addToDepartment}
                        onChange={(e) => setInviteData(prev => ({
                          ...prev,
                          addToDepartment: e.target.checked
                        }))}
                        disabled={!inviteData.selectedDepartment}
                      />
                    }
                    label="Add"
                  />
                </Grid>
                
                <Grid item xs={12} sm={10}>
                  <Autocomplete
                    id="team-select"
                    options={teams}
                    getOptionLabel={(team) => team.name}
                    onChange={handleTeamChange}
                    value={inviteData.selectedTeam}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Team" fullWidth />
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inviteData.addToTeam}
                        onChange={(e) => setInviteData(prev => ({
                          ...prev,
                          addToTeam: e.target.checked
                        }))}
                        disabled={!inviteData.selectedTeam}
                      />
                    }
                    label="Add"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormHelperText>
                    Select any combination of client, department, and team to add the user to. Check the corresponding "Add" checkbox for each selection.
                  </FormHelperText>
                </Grid>
              </>
            ) : (
              // Original group selection for new users
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="group-type-label">Group Type</InputLabel>
                    <Select
                      labelId="group-type-label"
                      name="type"
                      value={inviteData.type}
                      label="Group Type"
                      onChange={handleInviteInputChange}
                    >
                      <MenuItem value={1}>Team</MenuItem>
                      <MenuItem value={2}>Department</MenuItem>
                      <MenuItem value={3}>Client</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="group-select-label">Group</InputLabel>
                    <Select
                      labelId="group-select-label"
                      name="groupId"
                      value={inviteData.groupId}
                      label="Group"
                      onChange={handleInviteInputChange}
                    >
                      {inviteData.type === 1 && teams.map(team => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name}
                        </MenuItem>
                      ))}
                      {inviteData.type === 2 && departments.map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                      {inviteData.type === 3 && clients.map(client => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  name="role"
                  value={inviteData.role}
                  label="Role"
                  onChange={handleInviteInputChange}
                >
                  <MenuItem value={1}>Member</MenuItem>
                  <MenuItem value={2}>
                    {inviteData.type === 1 ? 'Lead' : 'Admin'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained" color="primary">
            {inviteData.isExistingUser ? 'Add User' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user "{currentUser?.full_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Invite Link Dialog */}
      <Dialog
        open={showInviteLink}
        onClose={handleCloseInviteLinkDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invitation Link Generated</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The invitation link has been generated successfully. You can copy it using the button below:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            value={inviteLink}
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { cursor: 'pointer' }
            }}
            onClick={handleCopyInviteLink}
            sx={{ 
              mt: 1,
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteLinkDialog}>Close</Button>
          <Button onClick={handleCopyInviteLink} variant="contained">
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 